import {
  Alert,
  Box,
  Button,
  Divider,
  IconButton,
  Typography,
  useTheme,
} from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { HOAI2021 } from './hoai-data/HOAI2021';
import { HOAI2013 } from './hoai-data/HOAI2013';
import { HOAI2009 } from './hoai-data/HOAI2009';
import ExoForm from '../ExoForm';
import TypeCustomSelect from '../../form/form-fields/TypeCustomSelect';
import { useTranslation } from 'react-i18next';
import MoneyLabel from '../../label/MoneyLabel';
import { useDebounce } from 'use-debounce';
import ExoDialog from '../ExoDialog';
import { CostAllocator } from '../Din276/CostAllocator';
import TypeText from '../../form/form-fields/TypeText';
import {
  API_GET,
  API_PATCH,
  API_POST,
  DBRequest,
  useConfig,
} from '../../../api/api';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import { HOAILoader } from './HOAILoader';
import { tokens } from '../../../global/theme/tokens';
import InfoIcon from '@mui/icons-material/Info';
import FormFields from '../../form/FormFields';

export const HOAICalculator = ({
  startDataCollection,
  noControls,
  onClose,
  onSubmit,
  hoaiTitle,
  debug = false,
}) => {
  const { t } = useTranslation();
  const config = useConfig();

  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);

  const logResults = debug;

  const hoais = [HOAI2021, HOAI2013, HOAI2009]; //In case of new HOAI import it and add it to this array
  //sort in reverse order (newest should have 0 array index)
  //HOAI INDEX DURCH JAHR ERSETZEN
  const formRef = useRef(null);
  const [dataCollection, setDataCollection] = useState({});
  const [startData, setStartData] = useState({});
  const [tooLowWarning, setTooLowWarning] = useState(false);
  const [phasesOver100, setPhasesOver100] = useState(false);
  const [openDIN, setOpenDIN] = useState(false);
  const [titleCollection, setTitleCollection] = useState({
    title: hoaiTitle ?? '',
  });

  // event debounce
  const [debounceData, setDebounceData] = useState(null);
  const [debounceCalc] = useDebounce(debounceData, 400);

  function getHoaiIndexByYear(year) {
    if (!year) return null;
    return hoais.findIndex((el) => el.year == year);
  }

  const [hoai, setHoai] = useState(
    startDataCollection && Object.keys(startDataCollection).length > 1
      ? hoais[getHoaiIndexByYear(startDataCollection.hoaiData.year)]
      : hoais[0]
  );
  const [selectedLabel, setSelectedLabel] = useState(
    startDataCollection && Object.keys(startDataCollection).length > 1
      ? startDataCollection.hoaiData.labelIndex
      : 0
  );

  const [openLoad, setOpenLoad] = useState(false);

  useEffect(() => {
    if (startDataCollection && Object.keys(startDataCollection).length > 1) {
      setTitleCollection({ title: startDataCollection.hoaiTitle });
      setSelectedLabel(startDataCollection.hoaiData.labelIndex);
      setHoai(hoais[getHoaiIndexByYear(startDataCollection.hoaiData.year)]);
      setDataCollection({
        ...startDataCollection.hoaiData,
        id: startDataCollection.id,
        hoaiIndex: getHoaiIndexByYear(startDataCollection.hoaiData.year),
      });

      calculateBaseHonorarium({
        ...startDataCollection.hoaiData,
        id: startDataCollection.id,
        hoaiIndex: getHoaiIndexByYear(startDataCollection.hoaiData.year),
      });
    } else if (
      config &&
      startDataCollection &&
      startDataCollection.id &&
      Object.keys(startDataCollection).length == 1
    ) {
      DBRequest({
        config,
        path: `hoai/${startDataCollection.id}`,
        method: API_GET,
        onResponse: setLoadedHOAI,
      });
      return;
    }

    const defaultData = {
      vat: startDataCollection?.defaultTaxRate ?? 19,
      hoaiIndex:
        startDataCollection && Object.keys(startDataCollection).length > 1
          ? getHoaiIndexByYear(startDataCollection.hoaiData.year)
          : 0,
      labelIndex:
        startDataCollection && Object.keys(startDataCollection).length > 1
          ? startDataCollection.hoaiData.labelIndex
          : selectedLabel,
      honorariumZone: 1,
      honorariumRate: 0,
      year:
        startDataCollection && Object.keys(startDataCollection).length > 1
          ? startDataCollection.hoaiData.year
          : hoais[0].year,
    };
    setStartData(
      startDataCollection && Object.keys(startDataCollection).length > 1
        ? {
            ...startDataCollection.hoaiData,
            id: startDataCollection.id,
          }
        : defaultData
    );
    setDataCollection(
      startDataCollection && Object.keys(startDataCollection).length > 1
        ? {
            ...startDataCollection.hoaiData,
            id: startDataCollection.id,
          }
        : defaultData
    );
  }, [startDataCollection, config]);

  const additionalCostField = {
    label: t('Additional Costs'),
    type: 'expandFields',
    colspan: 2,
    className: 'flex flex-col w-full md:grid md:grid-cols-2 gap-4',
    fields: [
      /*{
        colspan: 2,
        type: 'iconField',
        icon: InfoIcon,
        iconSize: 'medium',
        iconColor: '',
        tooltip:
          'Zuschlag enthält den Umbau- oder Modernisierungszuschlag nach §6 Abschnitt 5 und andere sonstige Zuschläge.',
        field: {
          label: t('Surcharge') + ' in %',
          key: 'surcharge',
          type: 'number',
        },
      },*/
      {
        label: t('Surcharge') + ' in %',
        key: 'surcharge',
        type: 'number',
        colspan: 2,
        Icon: InfoIcon,
        iconTooltip:
          'Zuschlag enthält den Umbau- oder Modernisierungszuschlag nach §6 Abschnitt 5 und andere sonstige Zuschläge.',
      },
      {
        label: t('Time fee') + ' in €',
        key: 'timeFee',
        type: 'number',
      },
      {
        label: t('Special Services') + ' in €',
        key: 'specialServices',
        type: 'number',
      },
      {
        label: t('Additional Services') + ' in €',
        key: 'additionalServices',
        type: 'number',
      },
      {
        label: t('Incidental Costs') + ' in %',
        key: 'incidentalCosts',
        type: 'number',
      },
      /*{
        label: t('Building substance to be processed') + ' in %',
        key: 'buildingSubstance',
        type: 'number',
      },
      {
        label: t('Share of TGA (technical building equipment)') + ' in %',
        key: 'tgaShare',
        type: 'number',
      },*/
      /*{
        label: t('VAT') + ' in %',
        key: 'vat',
        type: 'number',
      },*/
    ],
  };

  function generateRateOptions(hoai) {
    const options = hoai.rateOptions.map((el) => {
      return { label: t(el.label), value: el.value };
    });

    return options;
  }

  const [fields, setFields] = useState([
    {
      label: hoai.data[0].unitLabel,
      key: 'baseAmount',
      type: hoai.data[0].unit == 'euro' ? 'money' : 'number',
      colspan: 2,
    },
    {
      label: t('Honorarium Zone'),
      key: 'honorariumZone',
      type: 'customSelect',
      options: generateHonorariumZone(selectedLabel),
      noUnselect: true,
    },
    {
      label: t('Honorarium rate'),
      key: 'honorariumRate',
      type: 'numberSelect',
      noUnselect: true,
      options: generateRateOptions(hoai),
      unit: '%',
    },
    additionalCostField,
    {
      type: 'hoaiPhases',
      baseHonorariumKey: 'baseHonorarium',
      resultingHonorariumKey: 'resultingHonorarium',
      phases: hoai.data[0].phases,
      colspan: 2,
    },
  ]);

  function generateHOAIOptions() {
    return hoais.map((elem, index) => {
      return { label: elem.year, value: index };
    });
  }

  function generateLabelOptions() {
    return hoai.data.map((elem, index) => {
      return { label: elem.label, value: index };
    });
  }

  function generateHonorariumZone(selectedLabel) {
    const keys = Object.keys(hoai.data[selectedLabel].honorariumZone);
    return keys.map((label, index) => {
      return { label: label, value: index + 1 };
    });
  }

  function updateFields(selectedLabel, selectedHOAI, loadedDataCollection) {
    const hoai = hoais[selectedHOAI];

    const newFields = [
      {
        label: hoai.data[selectedLabel].unitLabel,
        key: 'baseAmount',
        type: hoai.data[selectedLabel].unit == 'euro' ? 'money' : 'number',
        colspan: 2,
      },
      {
        label: t('Honorarium Zone'),
        key: 'honorariumZone',
        type: 'customSelect',
        options: generateHonorariumZone(selectedLabel),
        noUnselect: true,
      },
      {
        label: t('Honorarium rate'),
        key: 'honorariumRate',
        type: 'numberSelect',
        noUnselect: true,
        options: generateRateOptions(hoai),
        unit: '%',
      },
      additionalCostField,
      {
        type: 'hoaiPhases',
        baseHonorariumKey: 'baseHonorarium',
        resultingHonorariumKey: 'resultingHonorarium',
        phases: hoai.data[selectedLabel].phases,
        colspan: 2,
      },
    ];

    if (loadedDataCollection) {
      setDataCollection({ ...loadedDataCollection, hoaiIndex: selectedHOAI });
      formRef.current.updateDataCollection({
        ...loadedDataCollection,
        hoaiIndex: selectedHOAI,
        year: hoais[selectedHOAI].year,
      });
    } else {
      setDataCollection({
        ...dataCollection,
        //phases: [],
        labelIndex: selectedLabel,
        hoaiIndex: selectedHOAI,
        year: hoais[selectedHOAI].year,
      });
      //formRef.current.updateDataCollection({
      //  //phases: [],
      //  hoaiIndex: selectedHOAI,
      //});
    }
    setHoai(hoai);
    setFields(newFields);
  }

  const hoaiVersionFields = {
    label: t('HOAI version'),
    key: 'hoaiIndex',
    options: generateHOAIOptions(),
    noUnselect: true,
  };

  const labelField = {
    label: '',
    key: 'labelIndex',
    options: generateLabelOptions(),
    noUnselect: true,
  };

  function handleHOAIChange(key, newHOAI) {
    handleLabelChange(null, 0, newHOAI);
  }

  function handleLabelChange(key, newLabel, newHOAI) {
    setSelectedLabel(newLabel);
    updateFields(
      newLabel,
      newHOAI ??
        dataCollection.hoaiIndex ??
        getHoaiIndexByYear(dataCollection.year) ??
        0,
      null
    );

    calculateBaseHonorarium(dataCollection, null, newLabel, newHOAI);
  }

  const findBoundingValues = (
    honorariumZone,
    selectedHonorariumZone,
    value
  ) => {
    const keys = Object.keys(honorariumZone[selectedHonorariumZone])
      .map(Number)
      .sort((a, b) => a - b);

    let previous = null;
    let next = null;

    for (let i = 0; i < keys.length; i++) {
      if (keys[i] == value) {
        if (i == 0) {
          previous = keys[i];
          next = keys[i + 1];
        } else {
          previous = keys[i - 1];
          next = keys[i];
        }
        break;
      }
      if (keys[i] < value) {
        previous = keys[i];
      }
      if (keys[i] > value) {
        next = keys[i];
        break;
      }
    }

    return { previous, next };
  };

  function calculateBaseHonorarium(data, hoaiIndex, newSelectedLabel, newHOAI) {
    const localHOAI = newHOAI || newHOAI == 0 ? hoais[newHOAI] : hoai;
    const localSelectedLabel = newSelectedLabel ?? selectedLabel;
    data = { ...data, hoaiIndex: hoaiIndex ?? dataCollection.hoaiIndex };

    if (data.honorariumRate > 100) {
      return formRef.current.updateDataCollection({
        honorariumRate: 100,
      });
    }
    if (data.honorariumRate < 0) {
      return formRef.current.updateDataCollection({
        honorariumRate: 0,
      });
    }

    setTooLowWarning(false);
    setPhasesOver100(false);

    var selectedHonorariumZone = Object.keys(
      localHOAI.data[localSelectedLabel].honorariumZone
    )[data.honorariumZone - 1];

    var realHonorariumZone = data.honorariumZone;

    //set honorarzone to 1 if the label changes
    if (
      !localHOAI.data[localSelectedLabel].honorariumZone[selectedHonorariumZone]
    ) {
      selectedHonorariumZone = 'I';
      realHonorariumZone = 1;
      if (!data.baseAmount) {
        return formRef.current.updateDataCollection({
          honorariumZone: realHonorariumZone,
        });
      }
    }

    if (!data.baseAmount) return;

    let baseAmountValues = Object.keys(
      localHOAI.data[localSelectedLabel].honorariumZone[selectedHonorariumZone]
    );

    baseAmountValues = baseAmountValues.sort((a, b) => Number(a) - Number(b));

    if (
      data.baseAmount > Number(baseAmountValues[baseAmountValues.length - 1])
    ) {
      return formRef.current.updateDataCollection({
        baseAmount: Number(baseAmountValues[baseAmountValues.length - 1]),
      });
    }

    let baseAmount = data.baseAmount;

    if (data.baseAmount < Number(baseAmountValues[0])) {
      setTooLowWarning(true);
      baseAmount = 0;
    }

    const phases =
      data.phases && data.phases.length > 0
        ? data.phases
        : localHOAI.data[localSelectedLabel].phases;

    const totalPhasePercent = phases.reduce(
      (sum, phase) =>
        phase.isCrossed !== true
          ? sum +
            (phase.value !== undefined
              ? Number(phase.value)
              : Number(phase.maxPercent))
          : sum,
      0
    );

    logResults && console.log('TotalPhasePercent: ', totalPhasePercent);

    if (totalPhasePercent > 100) {
      setPhasesOver100(true);
    }

    const honorariumRange = findBoundingValues(
      localHOAI.data[localSelectedLabel].honorariumZone,
      selectedHonorariumZone,
      Number(baseAmount)
    );

    // update the datacollection for out of range values
    if (!honorariumRange.previous || !honorariumRange.next) {
      setDataCollection({
        ...data,
        labelIndex: localSelectedLabel,
        baseHonorarium: 0,
        resultingHonorarium: 0,
        incidentalCostAmount: 0,
        vatAmount: 0,
        nettoHonorarium: 0,
        honorariumZone: realHonorariumZone,
      });

      formRef.current.updateDataCollection({
        baseHonorarium: 0,
        resultingHonorarium: 0,
        incidentalCostAmount: 0,
        vatAmount: 0,
        nettoHonorarium: 0,
        honorariumZone: realHonorariumZone,
      });

      return;
    }

    const prevTableHonorarium = honorariumRange.previous;
    const nextTableHonorarium = honorariumRange.next;

    const previousBaseHonorarium =
      localHOAI.data[localSelectedLabel].honorariumZone[selectedHonorariumZone][
        honorariumRange.previous
      ].min;
    const previousUpperHonorarium =
      localHOAI.data[localSelectedLabel].honorariumZone[selectedHonorariumZone][
        honorariumRange.previous
      ].max;
    const nextBaseHonorarium =
      localHOAI.data[localSelectedLabel].honorariumZone[selectedHonorariumZone][
        honorariumRange.next
      ].min;

    const nextUpperHonorarium =
      localHOAI.data[localSelectedLabel].honorariumZone[selectedHonorariumZone][
        honorariumRange.next
      ].max;

    const interpolatedLowerHonorarium =
      previousBaseHonorarium +
      ((baseAmount - honorariumRange.previous) *
        (nextBaseHonorarium - previousBaseHonorarium)) /
        (honorariumRange.next - honorariumRange.previous);

    const interpolatedUpperHonorarium =
      previousUpperHonorarium +
      ((baseAmount - honorariumRange.previous) *
        (nextUpperHonorarium - previousUpperHonorarium)) /
        (honorariumRange.next - honorariumRange.previous);

    const baseHonorarium =
      Math.round(
        100 *
          (interpolatedLowerHonorarium +
            ((interpolatedUpperHonorarium - interpolatedLowerHonorarium) *
              data.honorariumRate) /
              100)
      ) / 100;

    logResults && console.log('BaseAmount: ', baseAmount);

    logResults && console.log('HonorariumRange: ', honorariumRange);

    logResults &&
      console.log(
        'InterpolatedLowerBaseHonorarium: ',
        interpolatedLowerHonorarium
      );

    logResults &&
      console.log(
        'InterpolatedUpperBaseHonorarium: ',
        interpolatedUpperHonorarium
      );

    logResults && console.log('Base Honorarium: ', baseHonorarium);

    let resultingHonorarium =
      Math.round((100 * baseHonorarium * totalPhasePercent) / 100) / 100;

    logResults &&
      console.log('ResultingHonorariumAfterPhases: ', resultingHonorarium);

    //Zuschlag
    const surchargeAmount = data.surcharge
      ? Math.round(100 * resultingHonorarium * (Number(data.surcharge) / 100)) /
        100
      : '';

    resultingHonorarium = data.surcharge
      ? Math.round(
          100 * resultingHonorarium * (Number(data.surcharge) / 100 + 1)
        ) / 100
      : resultingHonorarium;

    logResults &&
      console.log('ResultingHonorariumAfterSurcharge: ', resultingHonorarium);

    //Zeithonorar
    resultingHonorarium = data.timeFee
      ? resultingHonorarium + Number(data.timeFee)
      : resultingHonorarium;

    logResults &&
      console.log('ResultingHonorariumAfterTimeFee: ', resultingHonorarium);

    //Besondere Leistungen
    resultingHonorarium = data.specialServices
      ? resultingHonorarium + Number(data.specialServices)
      : resultingHonorarium;

    logResults &&
      console.log(
        'ResultingHonorariumAfterSpecialServices: ',
        resultingHonorarium
      );

    //Zusätzliche Leistungen
    resultingHonorarium = data.additionalServices
      ? resultingHonorarium + Number(data.additionalServices)
      : resultingHonorarium;

    logResults &&
      console.log(
        'ResultingHonorariumAfterAdditionalServices: ',
        resultingHonorarium
      );

    //Nebenkosten
    const incidentalCostAmount = data.incidentalCosts
      ? Math.round(
          100 * resultingHonorarium * (Number(data.incidentalCosts) / 100)
        ) / 100
      : '';

    resultingHonorarium = data.incidentalCosts
      ? Math.round(
          100 * resultingHonorarium * (Number(data.incidentalCosts) / 100 + 1)
        ) / 100
      : resultingHonorarium;

    logResults &&
      console.log(
        'ResultingHonorariumAfterIncidentalCosts: ',
        resultingHonorarium
      );

    /*const buildingSubstanceAmount = data.buildingSubstance
      ? Math.round(
          100 * resultingHonorarium * (Number(data.buildingSubstance) / 100)
        ) / 100
      : '';*/

    resultingHonorarium = data.buildingSubstance
      ? Math.round(
          100 * resultingHonorarium * (Number(data.buildingSubstance) / 100 + 1)
        ) / 100
      : resultingHonorarium;

    /*const tgaShareAmount = data.tgaShare
      ? Math.round(100 * resultingHonorarium * (Number(data.tgaShare) / 100)) /
        100
      : '';*/

    resultingHonorarium = data.tgaShare
      ? Math.round(
          100 * resultingHonorarium * (Number(data.tgaShare) / 100 + 1)
        ) / 100
      : resultingHonorarium;

    const nettoHonorarium = resultingHonorarium;

    //Mehrwertsteuer
    const vatAmount = dataCollection.vat
      ? Math.round(
          100 * resultingHonorarium * (Number(dataCollection.vat) / 100)
        ) / 100
      : '';

    resultingHonorarium = dataCollection.vat
      ? Math.round(
          100 * resultingHonorarium * (Number(dataCollection.vat) / 100 + 1)
        ) / 100
      : resultingHonorarium;

    logResults &&
      console.log('ResultingHonorariumAfterVat: ', resultingHonorarium);
    logResults && console.log('--------------');

    logResults &&
      console.log('FinalResultingHonorarium: ', resultingHonorarium);

    const newPhases = phases.map((phase) => {
      return {
        ...phase,
        phaseHonorarium:
          Math.round(
            (100 * baseHonorarium * Number(phase.value ?? phase.maxPercent)) /
              100
          ) / 100,
      };
    });

    if (
      data.resultingHonorarium != resultingHonorarium ||
      data.showSiemon != dataCollection.showSiemon
    ) {
      setDataCollection({
        ...data,
        label: localHOAI.data[localSelectedLabel].label,
        labelIndex: localSelectedLabel,
        baseHonorarium: baseHonorarium,
        resultingHonorarium: resultingHonorarium,
        surchargeAmount: surchargeAmount,
        timeFee: data.timeFee,
        specialServices: data.specialServices,
        additionalServices: data.additionalServices,
        incidentalCostAmount: incidentalCostAmount,
        //buildingSubstance: data.buildingSubstance,
        //buildingSubstanceAmount: buildingSubstanceAmount,
        //tgaShareAmount: tgaShareAmount,
        vat: dataCollection.vat,
        vatAmount: vatAmount,
        nettoHonorarium: nettoHonorarium,
        honorariumZone: realHonorariumZone,
        phases: newPhases,
        previousBaseHonorarium: previousBaseHonorarium,
        previousUpperHonorarium: previousUpperHonorarium,
        nextBaseHonorarium: nextBaseHonorarium,
        nextUpperHonorarium: nextUpperHonorarium,
        prevTableHonorarium: prevTableHonorarium,
        nextTableHonorarium: nextTableHonorarium,
        showSiemon: data.showSiemon,
      });

      formRef.current.updateDataCollection({
        label: localHOAI.data[localSelectedLabel].label,
        baseHonorarium: baseHonorarium,
        resultingHonorarium: resultingHonorarium,
        surchargeAmount: surchargeAmount,
        timeFee: data.timeFee,
        specialServices: data.specialServices,
        additionalServices: data.additionalServices,
        incidentalCostAmount: incidentalCostAmount,
        //buildingSubstance: data.buildingSubstance,
        //buildingSubstanceAmount: buildingSubstanceAmount,
        //tgaShareAmount: tgaShareAmount,
        vat: dataCollection.vat,
        vatAmount: vatAmount,
        nettoHonorarium: nettoHonorarium,
        honorariumZone: realHonorariumZone,
        phases: newPhases,
        //showSiemon: data.showSiemon ?? false,
      });
    }
  }

  useEffect(() => {
    if (debounceData) {
      calculateBaseHonorarium(debounceData);
    }
  }, [
    debounceCalc?.baseAmount,
    debounceCalc?.honorariumRate,
    debounceCalc?.honorariumZone,
    debounceCalc?.surcharge,
    debounceCalc?.timeFee,
    debounceCalc?.specialServices,
    debounceCalc?.additionalServices,
    debounceCalc?.incidentalCosts,
    //debounceCalc?.buildingSubstance,
    //debounceCalc?.tgaShare,
    debounceCalc?.vat,
    debounceCalc?.phases,
    debounceCalc?.showSiemon,
  ]);

  function handleDIN(res) {
    setDataCollection({
      ...dataCollection,
      baseAmount: res.baseAmount,
      dinData: res.dinData,
      dinTitle: res.dinTitle,
      dinId: res.dinId,
      dinYear: res.year,
    });
    formRef.current.updateDataCollection({
      baseAmount: res.baseAmount,
      dinData: res.dinData,
      dinTitle: res.dinTitle,
      dinId: res.dinId,
      dinYear: res.year,
    });

    calculateBaseHonorarium(formRef.current.dataCollection);
  }

  function handleSubmit() {
    const data = {
      nettoHonorarium: dataCollection.nettoHonorarium,
      baseHonorarium: dataCollection.baseHonorarium,
      title: titleCollection.title,
      hoaiData: dataCollection,
      dinId: dataCollection.dinId,
      year: dataCollection.year,
      validFrom: hoai.validFrom,
      baseAmountUnit: hoai.data[selectedLabel].unit,
      label: dataCollection.label,
      baseAmount: dataCollection.baseAmount,
      honorariumZone: dataCollection.honorariumZone,
      honorariumRate: dataCollection.honorariumRate,
      previousBaseHonorarium: dataCollection.previousBaseHonorarium,
      previousUpperHonorarium: dataCollection.previousUpperHonorarium,
      nextBaseHonorarium: dataCollection.nextBaseHonorarium,
      nextUpperHonorarium: dataCollection.nextUpperHonorarium,
      prevTableHonorarium: dataCollection.prevTableHonorarium,
      nextTableHonorarium: dataCollection.nextTableHonorarium,
      surcharge: dataCollection.surcharge,
      timeFee: dataCollection.timeFee,
      specialServices: dataCollection.specialServices,
      additionalServices: dataCollection.additionalServices,
      incidentalCosts: dataCollection.incidentalCosts,
      //buildingSubstance: dataCollection.buildingSubstance,
      //tgaShare: dataCollection.tgaShare,
      vat: dataCollection.vat,
    };

    DBRequest({
      config,
      path: dataCollection?.id ? `hoai/${dataCollection.id}` : `hoai`,
      method: dataCollection?.id ? API_PATCH(data) : API_POST(data),
      onResponse: handleResponse,
    });
  }

  function handleResponse(res) {
    setDataCollection({ id: res.data.id, ...dataCollection });
    onSubmit({
      hoaiData: res.data.hoaiData,
      hoaiTitle: res.data.title,
      id: res.data.id,
      hoaiTotalCost: res.data.totalCost,
      oldHoaiId: startDataCollection?.id,
    });

    onClose();
  }

  function handleTitleChange(key, value) {
    setTitleCollection({ title: value });
  }

  function setLoadedHOAI(data) {
    setOpenLoad(false);
    setTitleCollection({ title: data.data.title });
    setSelectedLabel(data.data.hoaiData.labelIndex);
    updateFields(
      data.data.hoaiData.labelIndex,
      getHoaiIndexByYear(data.data.hoaiData.year),
      {
        ...data.data.hoaiData,
        id: data.data.id,
      }
    );

    calculateBaseHonorarium(
      data.data.hoaiData,
      getHoaiIndexByYear(data.data.hoaiData.year)
    );
    setStartData({ id: data.data.id, ...data.data.hoaiData });
  }

  return (
    <Box>
      {debug && (
        <Button
          color="warning"
          variant="contained"
          onClick={() => console.log(dataCollection)}
          sx={{
            position: 'absolute',
            top: '0',
            left: '0',
          }}
        >
          LOG DATA
        </Button>
      )}
      <ExoDialog open={openDIN}>
        {openDIN && (
          <CostAllocator
            startData={dataCollection?.dinData}
            onClose={() => setOpenDIN(false)}
            onSubmit={handleDIN}
            dinTitle={dataCollection?.dinTitle}
            hoaiTitle={titleCollection.title}
            dinId={dataCollection?.dinId}
            startDinYear={dataCollection?.dinYear || hoai.correspondingDin}
            shownCostGroups={hoai.data[selectedLabel].shownCostGroups}
            disabledCostGroups={hoai.data[selectedLabel].disabledCostGroups}
            additionalFields={hoai.data[selectedLabel].additionalFields}
            creditableCostFunction={
              hoai.data[selectedLabel].creditableCostFunction
            }
          />
        )}
      </ExoDialog>
      <ExoDialog open={openLoad} onClose={() => setOpenLoad(false)}>
        <HOAILoader onSelect={setLoadedHOAI} />
      </ExoDialog>
      <Box className="flex justify-between">
        <Typography fontSize={24} fontWeight={500}>
          {t('HOAI Calculator')}
        </Typography>
        <IconButton onClick={() => setOpenLoad(true)}>
          <CloudDownloadIcon />
        </IconButton>
      </Box>
      <Box className="flex flex-col md:grid md:grid-cols-3 gap-4 my-3 mt-6">
        <TypeCustomSelect
          field={hoaiVersionFields}
          dataCollection={dataCollection}
          startDataCollection={startData}
          errors={{}}
          updateValidationOnChange={handleHOAIChange}
        />
        <Box className="w-full flex gap-4 md:col-span-2">
          <TypeCustomSelect
            field={labelField}
            dataCollection={dataCollection}
            startDataCollection={startData}
            errors={{}}
            updateValidationOnChange={handleLabelChange}
          />
          <Button
            variant="outlined"
            //color={'secondary'}
            onClick={() => setOpenDIN(true)}
            disabled={!hoai.data[selectedLabel].shownCostGroups}
            disableElevation
            sx={{
              backgroundColor: /*noBackground ? 'unset' :*/ colors.bgInput,
              ':hover': {
                cursor: /*onClick ? 'pointer' :*/ 'auto',
                border:
                  /*label
                  ? 'unset'
                  : */ '1px solid ' + colors.info + '80!important',
              },
              'fieldset:hover': {
                border: '1px solid ' + colors.info + '80!important',
              },
              border: /*label ? 'unset' :*/ `1px solid ${colors.selected}`,
            }}
          >
            {t('Creditable Costs from DIN 276')}
          </Button>
        </Box>
      </Box>
      <ExoForm
        debug={debug}
        outerClassName="h-fit"
        className="flex flex-col w-full md:grid md:grid-cols-2 gap-4"
        ref={formRef}
        fields={fields}
        noControls={true}
        startDataCollection={startData}
        onChange={setDebounceData}
      />

      {Boolean(dataCollection.surchargeAmount) && (
        <AdditionalCostLine
          label={t('Surcharge')}
          amount={dataCollection.surchargeAmount}
          percent={dataCollection.surcharge}
        />
      )}
      {Boolean(dataCollection.timeFee) && (
        <AdditionalCostLine
          label={t('Time fee')}
          amount={dataCollection.timeFee}
        />
      )}
      {Boolean(dataCollection.specialServices) && (
        <AdditionalCostLine
          label={t('Special Services')}
          amount={dataCollection.specialServices}
        />
      )}
      {Boolean(dataCollection.additionalServices) && (
        <AdditionalCostLine
          label={t('Additional Services')}
          amount={dataCollection.additionalServices}
        />
      )}
      {Boolean(dataCollection.incidentalCostAmount) && (
        <AdditionalCostLine
          label={t('Incidental Costs')}
          amount={dataCollection.incidentalCostAmount}
          percent={dataCollection.incidentalCosts}
        />
      )}
      {Boolean(dataCollection.buildingSubstanceAmount) && (
        <AdditionalCostLine
          label={t('Building substance to be processed')}
          amount={dataCollection.buildingSubstanceAmount}
          percent={dataCollection.buildingSubstance}
        />
      )}
      {Boolean(dataCollection.tgaShareAmount) && (
        <AdditionalCostLine
          label={t('Share of TGA')}
          amount={dataCollection.tgaShareAmount}
          percent={dataCollection.tgaShare}
        />
      )}
      <Divider />
      <Box className="flex justify-between pr-3 pl-2 mb-3 mt-3">
        <Typography fontWeight={600}>
          {t('Netto Honorarium including additional Costs')}
        </Typography>
        <Typography fontWeight={600}>
          <MoneyLabel money={dataCollection.nettoHonorarium} />
        </Typography>
      </Box>

      {/*Boolean(dataCollection.vatAmount) && (
        <AdditionalCostLine
          label={t('VAT')}
          amount={dataCollection.vatAmount}
          percent={dataCollection.vat}
        />
      )*/}
      <Box className="flex justify-between items-center">
        <Box className="w-[125px]">
          <FormFields
            dataCollection={dataCollection}
            updateDataCollection={(data) => {
              const newDataCollection = { ...dataCollection, vat: data.vat };
              setDataCollection(newDataCollection);
              calculateBaseHonorarium(newDataCollection);
            }}
            fields={[
              {
                size: 'small',
                label: t('VAT'),
                key: 'vat',
                type: 'number',
                unit: '%',
              },
            ]}
          />
        </Box>
        <Box className="flex justify-end pl-2 pr-3 mb-2">
          <Typography
            className="text-right"
            sx={{ width: '125px', minWidth: '125px' }}
          >
            <MoneyLabel money={dataCollection?.vatAmount ?? 0} />
          </Typography>
        </Box>
      </Box>
      <Box className="h-3" />
      <Divider />
      {tooLowWarning && (
        <Alert severity="error">
          {t('The given ') +
            hoai.data[selectedLabel].unitLabel +
            t(' are too low')}
        </Alert>
      )}
      {phasesOver100 && (
        <Alert severity="warning">{t('The phases surpass 100%')}</Alert>
      )}
      <Box className="flex justify-between pr-3 pl-2 mb-1 mt-5">
        <Typography fontWeight={600}>
          {t('Brutto Honorarium including additional Costs')}
        </Typography>
        <Typography fontWeight={600}>
          <MoneyLabel money={dataCollection.resultingHonorarium} />
        </Typography>
      </Box>
      <Box className="flex justify-end w-full px-2 pt-2">
        <Typography>{t('All results are without guarantee')}</Typography>
      </Box>
      {!noControls && (
        <Box className="flex justify-between py-5">
          <Button variant="contained" color="error" onClick={onClose}>
            {t('Cancel')}
          </Button>
          <Box className="flex items-center gap-2">
            <TypeText
              size="small"
              field={{ label: t('Save as'), key: 'title' }}
              dataCollection={titleCollection}
              errors={{}}
              updateValidationOnChange={handleTitleChange}
            />
            <Button
              className="h-full"
              variant="contained"
              color="success"
              onClick={handleSubmit}
              disabled={
                !titleCollection.title ||
                Object.keys(dataCollection).length == 0 ||
                !dataCollection.baseHonorarium ||
                !dataCollection.baseAmount ||
                dataCollection.baseHonorarium == 0 ||
                dataCollection.baseAmount == 0 ||
                dataCollection.vat === null ||
                dataCollection.vat === undefined
              }
            >
              {t('Save')}
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export const AdditionalCostLine = ({ label, amount, percent }) => {
  return (
    <Box className="flex justify-end pl-2 pr-3 mb-2">
      <Typography className="text-left w-full">
        {`${label} ${percent ? `(${percent}%)` : ''}`}
      </Typography>
      <Typography
        className="text-right"
        sx={{ width: '125px', minWidth: '125px' }}
      >
        <MoneyLabel money={amount} />
      </Typography>
    </Box>
  );
};
